import React, { useEffect, useState } from 'react';
import AdminPageTitle from '../components/AdminPageTitle';
import { fetchPaymentInformation, createPaymentInformation, updatePaymentInformation } from '../services/paymentInfo';
import './Currency.css';

const PaymentInfo = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState({
    account_name: '',
    account_number: '',
    bank_name: '',
    is_active: true,
  });

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchPaymentInformation();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load payment information: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        account_name: formData.account_name.trim(),
        account_number: formData.account_number.trim(),
        bank_name: formData.bank_name.trim(),
        is_active: !!formData.is_active,
      };

      if (editingId) {
        await updatePaymentInformation(editingId, payload);
        setSuccess('Payment information updated successfully!');
        setEditingId(null);
      } else {
        await createPaymentInformation(payload);
        setSuccess('Payment information saved successfully!');
      }
      setFormData({
        account_name: '',
        account_number: '',
        bank_name: '',
        is_active: true,
      });
      loadItems();
    } catch (err) {
      setError('Failed to save payment information: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({
      account_name: item.account_name || '',
      account_number: item.account_number || '',
      bank_name: item.bank_name || '',
      is_active: !!item.is_active,
    });
    setSuccess(null);
    setError(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setFormData({
      account_name: '',
      account_number: '',
      bank_name: '',
      is_active: true,
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div className="currency-management">
      <AdminPageTitle title="Payment Info" />
      <div className="currency-header">
        <h1>Payment Information</h1>
      </div>

      {error && (
        <div
          className="alert alert-error"
          style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#fee', color: '#c33', borderRadius: '4px' }}
        >
          {error}
        </div>
      )}

      {success && (
        <div
          className="alert alert-success"
          style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#efe', color: '#3c3', borderRadius: '4px' }}
        >
          {success}
        </div>
      )}

      <div className="table-container" style={{ marginBottom: '2rem' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Account Name</th>
              <th>Account Number</th>
              <th>Bank Name</th>
              <th>Active</th>
              <th>Created At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6}>Loading...</td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={6}>No payment information found. Add one below.</td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id}>
                  <td>{item.account_name}</td>
                  <td>{item.account_number}</td>
                  <td>{item.bank_name}</td>
                  <td>{item.is_active ? 'Yes' : 'No'}</td>
                  <td>{formatDate(item.created_at)}</td>
                  <td>
                    <button
                      type="button"
                      className="btn-secondary"
                      style={{ padding: '0.35rem 0.75rem', fontSize: '0.9rem' }}
                      onClick={() => handleEdit(item)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="currency-header" style={{ marginBottom: '1rem' }}>
        <h2>{editingId ? 'Update Payment Information' : 'Add Payment Information'}</h2>
      </div>

      <form onSubmit={handleSubmit} className="currency-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="account_name">Account Name</label>
            <input
              type="text"
              id="account_name"
              name="account_name"
              value={formData.account_name}
              onChange={handleChange}
              required
              placeholder="FM Ultratech Farms Ltd"
            />
          </div>
          <div className="form-group">
            <label htmlFor="account_number">Account Number</label>
            <input
              type="text"
              id="account_number"
              name="account_number"
              value={formData.account_number}
              onChange={handleChange}
              required
              placeholder="0123456789"
            />
          </div>
          <div className="form-group">
            <label htmlFor="bank_name">Bank Name</label>
            <input
              type="text"
              id="bank_name"
              name="bank_name"
              value={formData.bank_name}
              onChange={handleChange}
              required
              placeholder="Bank Name"
            />
          </div>
        </div>

        <div className="form-row">
          <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
            />
            <span>Set as active payment information</span>
          </label>
        </div>

        <div className="modal-actions" style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button type="submit" className="btn-primary">
            {editingId ? 'Update Payment Information' : 'Save Payment Information'}
          </button>
          {editingId && (
            <button type="button" className="btn-secondary" onClick={handleCancelEdit}>
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default PaymentInfo;

